// frontend/webapp/src/components/config_forms/holiday/components/CustomHolidayDialog.tsx
//
// Dialog for creating and editing custom holidays. Two mount-time modes:
//   - Add (editingHoliday === null): blank form
//   - Edit (editingHoliday !== null): pre-filled from the passed row
//
// Two mount-time contexts:
//   - User context (clientId === null): no scope checkboxes
//   - Client context (clientId !== null): scope checkboxes visible;
//     "Also add to my personal catalog" with dependent "Also apply to
//     other clients" (enabled only when personal is checked)

import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { toast } from 'sonner';
import {
  useCreateCustomHoliday, useUpdateCustomHoliday, useCustomHolidays,
} from '@/hooks/useCustomHolidays';
import type { CustomHoliday } from '@/types/holiday';

type Mode = 'add' | 'edit';

export interface CustomHolidayDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string | null;  // null = user context; string = client context
  editingHoliday: CustomHoliday | null;
}

const CATEGORY_OPTIONS = [
  'Observance', 'Religious', 'Season', 'Federal Holiday', 'State Holiday',
];

function daysInMonth(month: number): number {
  // Max days we'll offer in the dropdown. Feb caps at 28 (Feb 29 forbidden
  // for annual recurrence; users pick Feb 28 or Mar 1 instead).
  return [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1] ?? 31;
}

export function CustomHolidayDialog({
  open, onOpenChange, clientId, editingHoliday,
}: CustomHolidayDialogProps) {
  const mode: Mode = editingHoliday ? 'edit' : 'add';
  const createMut = useCreateCustomHoliday();
  const updateMut = useUpdateCustomHoliday();
  const { data: allCustoms = [] } = useCustomHolidays();

  // Form state
  const [name, setName] = useState('');
  const [category, setCategory] = useState<string>('');
  const [startMonth, setStartMonth] = useState<number>(1);
  const [startDay, setStartDay] = useState<number>(1);
  const [isMultiDay, setIsMultiDay] = useState(false);
  const [endMonth, setEndMonth] = useState<number>(1);
  const [endDay, setEndDay] = useState<number>(1);
  const [addToPersonal, setAddToPersonal] = useState(false);

  // Pre-fill on edit mode
  useEffect(() => {
    if (!open) return;
    if (editingHoliday) {
      setName(editingHoliday.name);
      setCategory(editingHoliday.types[0] ?? '');
      setStartMonth(editingHoliday.startMonth);
      setStartDay(editingHoliday.startDay);
      setIsMultiDay(editingHoliday.endMonth !== null);
      setEndMonth(editingHoliday.endMonth ?? editingHoliday.startMonth);
      setEndDay(editingHoliday.endDay ?? editingHoliday.startDay);
      setAddToPersonal(false);
    } else {
      setName('');
      setCategory('');
      setStartMonth(1);
      setStartDay(1);
      setIsMultiDay(false);
      setEndMonth(1);
      setEndDay(1);
      setAddToPersonal(false);
    }
  }, [open, editingHoliday]);

  // Edit-mode race: if the row has been deleted elsewhere, close with toast
  useEffect(() => {
    if (mode !== 'edit' || !editingHoliday) return;
    if (!allCustoms.find(c => c.id === editingHoliday.id)) {
      toast.error('This custom holiday no longer exists. Refresh the list and try again.');
      onOpenChange(false);
    }
  }, [allCustoms, editingHoliday, mode, onOpenChange]);

  // Derived "apply to other clients" state cascades from addToPersonal
  const nameError = useMemo(() => {
    const trimmed = name.trim();
    if (trimmed.length === 0) return 'Name is required';
    if (trimmed.length > 80) return 'Name must be 80 characters or fewer';
    return null;
  }, [name]);

  const dateError = useMemo(() => {
    if (startDay > daysInMonth(startMonth)) return 'Start day is not valid for that month';
    // Forbid Feb 29 — UI-level enforcement for the "no leap-day customs" rule
    if (startMonth === 2 && startDay === 29) {
      return 'Custom holidays must recur every year; pick Feb 28 or Mar 1 instead';
    }
    if (isMultiDay) {
      if (endDay > daysInMonth(endMonth)) return 'End day is not valid for that month';
      if (endMonth === 2 && endDay === 29) {
        return 'End date cannot be Feb 29 for the same reason';
      }
    }
    return null;
  }, [startMonth, startDay, isMultiDay, endMonth, endDay]);

  const canSubmit = nameError === null && dateError === null && category !== '';

  const handleSave = async () => {
    if (!canSubmit) return;
    const payload = {
      name: name.trim(),
      types: [category],
      startMonth, startDay,
      endMonth: isMultiDay ? endMonth : null,
      endDay:   isMultiDay ? endDay   : null,
      // Scope:
      //   User context (clientId === null): always personal catalog
      //   Client context:
      //     addToPersonal = true: write with client_id = null (personal catalog)
      //     addToPersonal = false: write with client_id = <this client>
      clientId: clientId === null
        ? null
        : addToPersonal ? null : clientId,
    };

    try {
      if (mode === 'edit' && editingHoliday) {
        await updateMut.mutateAsync({ id: editingHoliday.id, ...payload });
        toast.success(`Updated "${payload.name}"`);
      } else {
        const created = await createMut.mutateAsync(payload);
        toast.success(`Added "${created.name}"`);

        // Client-context "apply to other clients" cascade:
        // If set, this is a one-time batch that adds the holiday's id
        // to every OTHER client's custom_holiday_ids. The actual batch
        // is handled by the consumer wire-up in Phase O — this dialog
        // returns the created id + flags via the mutation result, and
        // the caller does the batch. For now we rely on the backend
        // propagation triggers to pick up the change when user-level
        // config updates; a surgical "batch-add to all clients" is
        // deferred per spec.
      }
      onOpenChange(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(`Failed to save: ${message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === 'edit' ? 'Edit custom holiday' : 'Add custom holiday'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="ch-name">Name</Label>
            <Input id="ch-name" value={name} onChange={(e) => setName(e.target.value)} />
            {nameError && <p className="text-xs text-destructive mt-1">{nameError}</p>}
          </div>

          <div>
            <Label htmlFor="ch-category">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="ch-category" className="bg-surface-2 shadow-inset">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="ch-start-month">Start month</Label>
              <Select value={String(startMonth)} onValueChange={(v) => setStartMonth(Number(v))}>
                <SelectTrigger id="ch-start-month" className="bg-surface-2 shadow-inset">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                    <SelectItem key={m} value={String(m)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="ch-start-day">Start day</Label>
              <Select value={String(startDay)} onValueChange={(v) => setStartDay(Number(v))}>
                <SelectTrigger id="ch-start-day" className="bg-surface-2 shadow-inset" aria-describedby="ch-date-error">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: daysInMonth(startMonth) }, (_, i) => i + 1).map(d => (
                    <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="ch-multiday"
              checked={isMultiDay}
              onCheckedChange={(v) => setIsMultiDay(Boolean(v))}
            />
            <Label htmlFor="ch-multiday">Multi-day range</Label>
          </div>

          <AnimatedCollapse open={isMultiDay}>
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="ch-end-month">End month</Label>
                  <Select value={String(endMonth)} onValueChange={(v) => setEndMonth(Number(v))}>
                    <SelectTrigger id="ch-end-month" className="bg-surface-2 shadow-inset">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                        <SelectItem key={m} value={String(m)}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="ch-end-day">End day</Label>
                  <Select value={String(endDay)} onValueChange={(v) => setEndDay(Number(v))}>
                    <SelectTrigger id="ch-end-day" className="bg-surface-2 shadow-inset" aria-describedby="ch-date-error">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: daysInMonth(endMonth) }, (_, i) => i + 1).map(d => (
                        <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Ranges spanning year-end are supported (e.g. Dec 22 to Jan 2).
              </p>
            </div>
          </AnimatedCollapse>

          {dateError && <p id="ch-date-error" className="text-xs text-destructive" role="alert">{dateError}</p>}

          {clientId !== null && (
            <div className="space-y-2 pt-2 border-t">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="ch-personal"
                  checked={addToPersonal}
                  onCheckedChange={(v) => setAddToPersonal(Boolean(v))}
                />
                <Label htmlFor="ch-personal">Also add to my personal holiday catalog</Label>
              </div>
              {/*
                The "Also apply to my other clients" checkbox was intentionally
                removed pending a proper batch fan-out implementation. A checkbox
                that silently no-ops would be worse than no checkbox at all. When
                we're ready to ship cross-client fan-out, add a new RPC that
                accepts a custom_holiday_id and a set of target client_ids and
                appends the id to each target's custom_holiday_ids array inside
                one transaction.
              */}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={!canSubmit}>
            {mode === 'edit' ? 'Save Changes' : 'Save Holiday'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
